import { useReactiveVar } from "@apollo/client";
import { usePathname } from "expo-router";
import { useMemo, useCallback } from "react";

import {
  useKanbanListMyUserWatchlistsQuery,
  type KanbanListMyUserWatchlistsQuery,
} from "@/generated/graphql";
import { watchListSelectedVar } from "@/stores/player";

import { MY_WATCHLISTS_PAGE_SIZE } from "@/features/watchlist/constants";

/**
 * Watchlist interface for internal component use
 */
interface Watchlist {
  id: string;
  name: string;
  lastModified?: string;
}

/**
 * Type for user watchlist edges from GraphQL query
 */
type UserWatchlistEdge = NonNullable<
  KanbanListMyUserWatchlistsQuery["listMyUserWatchlists"]
>["edges"][number];

/**
 * Hook that handles GraphQL watchlist queries and watchlist selection state
 * @returns Object with watchlist state, selection data, loading/error states, and change handler
 */
const useWatchlistQuery = () => {
  // Use the application's watchlist selection store
  const watchListSelected = useReactiveVar(watchListSelectedVar);
  const pathname = usePathname();

  // GraphQL query for watchlists
  const {
    data: watchlistData,
    loading: watchlistsLoading,
    error: watchlistsError,
  } = useKanbanListMyUserWatchlistsQuery({
    // SE-7071: without an explicit `first` the backend's relay default (20)
    // silently capped the org's watchlist list, so players living only on
    // watchlist #21+ never reached the drawer's "no filter" aggregate. Same
    // defect class SE-3309 fixed at the other call sites of this resolver.
    variables: { first: MY_WATCHLISTS_PAGE_SIZE },
    onCompleted: data => {
      // Auto-select "All" option if none selected and we're in kanban route
      if (!watchListSelected && data?.listMyUserWatchlists?.edges?.length > 0) {
        // Only set "all" if we're actually in a route that renders KanbanBoard
        if (
          pathname?.includes("kanban") ||
          pathname?.includes("player-kanban") ||
          pathname?.includes("scouting")
        ) {
          watchListSelectedVar("all");
        }
      }
    },
  });

  // Process watchlists data - using the real watchlist structure
  const watchlists = useMemo((): Watchlist[] => {
    if (!watchlistData?.listMyUserWatchlists?.edges) return [];

    const userWatchlists = watchlistData.listMyUserWatchlists.edges.map(
      (edge: UserWatchlistEdge) => ({
        id: edge.node.watchlist.id,
        name: edge.node.watchlist.name,
        lastModified: edge.node.watchlist.updatedAt,
      })
    );

    return [
      {
        id: "all",
        name: "All Watchlists",
      },
      ...userWatchlists,
    ];
  }, [watchlistData]);

  // Determine which watchlist ID to use for player queries
  const queryWatchlistId = useMemo(() => {
    if (watchListSelected === "all") {
      // For "All", we'll use a special approach - start with the first watchlist
      return (
        watchlistData?.listMyUserWatchlists?.edges?.[0]?.node?.watchlist?.id ||
        ""
      );
    }
    return (
      watchListSelected ||
      watchlistData?.listMyUserWatchlists?.edges?.[0]?.node?.watchlist?.id ||
      ""
    );
  }, [watchListSelected, watchlistData]);

  // Get all watchlist IDs for "All" option queries
  const allWatchlistIds = useMemo(
    () =>
      watchlistData?.listMyUserWatchlists?.edges?.map(
        edge => edge.node.watchlist.id
      ) || [],
    [watchlistData]
  );

  const handleWatchlistChange = useCallback((watchlistId: string) => {
    // Update the global watchlist selection
    watchListSelectedVar(watchlistId);
  }, []);

  const selectedWatchlist = useMemo(() => {
    // Simply find the selected watchlist without auto-updating reactive variables
    const found = watchlists.find(w => w.id === watchListSelected);

    // If not found but we have watchlists, return the first one for display
    // but don't update the reactive variable as this can interfere with user selections
    if (!found && watchlists.length > 0) {
      return watchlists[0]; // "All Watchlists" should be first
    }

    return found || null;
  }, [watchlists, watchListSelected]);

  return {
    // State
    watchListSelected,
    watchlists,
    selectedWatchlist,
    queryWatchlistId,
    allWatchlistIds,

    // Loading/Error states
    watchlistsLoading,
    watchlistsError,

    // Handlers
    handleWatchlistChange,
  };
};

export default useWatchlistQuery;
